import { useEffect, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas-pro";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { makeRequest } from "app/utils/makeRequest";
import { getSessionStore } from "app/utils/sessionStore";
import { getThemeColors, isSafari } from "app/utils/utils";
import DownloadIcon from "app/assets/IconComponent/DownloadIcon";
import { ErrorPage } from "app/uicomponents/macrocomponents/ErrorPage";
import IRCIndexPage from "./IRCIndexPage";
import IRCSummaryPage from "./IRCSummaryPage";
import IRCFundingPage from "./IRCFundingPage";
import IRCPerformancePage from "./IRCPerformancePage";
import IRCSignaturesPage from "./IRCSignaturesPage";
import IRCDisclosurePage from "./IRCDisclosurePage";
import IRCAppendixPage from "./IRCAppendixPage";

const PDF_FONT_SIZE = isSafari() ? "9.7px" : "10.7px";
const a4Width = 595.28;
const a4Height = 841.89;

const IRCPageWrapper = ({ children }: { children: React.ReactNode }) => (
  <div
    className="border-1 border-gray-500 overflow-hidden"
    style={{ aspectRatio: "1 / 1.414", fontSize: PDF_FONT_SIZE }}
  >
    {children}
  </div>
);

const addPageToPdf = async (pdf: jsPDF, pageElement: HTMLElement) => {
  const originalStyle = pageElement.style.cssText;
  pageElement.style.cssText = `
    position: fixed;
    top: -9999px;
    left: -9999px;
    width: ${a4Width}px;
    height: ${a4Height}px;
    margin: 0;
    padding: 0;
  `;
  try {
    const canvas = await html2canvas(pageElement, {
      scale: 2,
      useCORS: true,
      imageTimeout: 10000,
      logging: false,
      allowTaint: true,
      width: a4Width,
      height: a4Height,
      windowWidth: a4Width,
      windowHeight: a4Height,
    });
    const imgData = canvas.toDataURL("image/png");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight, undefined, "FAST");
  } finally {
    pageElement.style.cssText = originalStyle;
  }
};

interface InteractiveReviewCenterPdfDownloadProps {
  planId: string;
  selectedYear?: number;
  onClose: () => void;
}

export default function InteractiveReviewCenterPdfDownload({
  planId,
  selectedYear,
  onClose,
}: InteractiveReviewCenterPdfDownloadProps) {
  const [pdfData, setPdfData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const year = selectedYear ?? getSessionStore("ircSelectedYear") ?? 1;
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor } = getThemeColors(branding);

  useEffect(() => {
    setLoading(true);
    makeRequest("interactiveReviewCenterPdfData")
      .get(`${planId}`, true)
      .then((res: any) => {
        setPdfData(res?.data?.data);
        setLoading(false);
      })
      .catch((err: any) => {
        setError(err?.message ?? "Failed to load PDF data");
        setLoading(false);
      });
  }, [planId, year]);

  const handleDownload = async () => {
    setDownloadLoading(true);
    try {
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF("p", "mm", "a4");
      const pages = document.querySelectorAll(".irc-pdf-page");
      for (let i = 0; i < pages.length; i++) {
        if (i > 0) pdf.addPage();
        // eslint-disable-next-line no-await-in-loop
        await addPageToPdf(pdf, pages[i] as HTMLElement);
      }
      pdf.save(`${pdfData?.summary?.proposalNo ?? "review-center"}.pdf`);
    } finally {
      setDownloadLoading(false);
    }
  };

  const content = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center" style={{ minHeight: "60vh" }}>
          <ProgressSpinner />
        </div>
      );
    }
    if (error) {
      return <ErrorPage errorMsg={error} height="h-17rem" />;
    }

    const { summary, funding, performance, signatures } = pdfData ?? {};

    // Signature split: advisor counts as 1 slot; keep ≤ 5 insureds on first page
    const allInsureds = signatures?.insureds ?? [];
    const needsSignatureSplit = allInsureds.length > 5;
    const sig1Insureds = allInsureds.slice(0, 5);
    const sig2Insureds = allInsureds.slice(5);

    // Pre-compute page numbers (index/cover has no number; numbered pages start at 1)
    let pg = 0;
    const summaryPage = ++pg; // 1
    const fundingPage = funding?.length > 0 ? ++pg : null; // 2 if present
    const postCreditPage = performance?.length > 0 ? ++pg : null; // 3 if present
    const preCreditPage = performance?.length > 0 ? ++pg : null; // 4 if present
    const sig1Page = signatures ? ++pg : null; // 5 if present
    const sig2Page = signatures && needsSignatureSplit ? ++pg : null; // 6 if split
    const disclosurePage = ++pg;
    const appendixPage = performance?.length > 0 ? ++pg : null;

    return (
      <div>
        {/* Index / Cover — no page number */}
        <IRCPageWrapper>
          <div className="irc-pdf-page h-full w-full">
            <IRCIndexPage summary={summary} />
          </div>
        </IRCPageWrapper>

        <IRCPageWrapper>
          <div className="irc-pdf-page h-full w-full">
            <IRCSummaryPage summary={summary} pageNumber={summaryPage} />
          </div>
        </IRCPageWrapper>

        {funding?.length > 0 && fundingPage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCFundingPage funding={funding} pageNumber={fundingPage} />
            </div>
          </IRCPageWrapper>
        )}

        {performance?.length > 0 && postCreditPage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCPerformancePage
                performance={performance}
                creditType="postCredit"
                pageNumber={postCreditPage}
              />
            </div>
          </IRCPageWrapper>
        )}

        {performance?.length > 0 && preCreditPage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCPerformancePage
                performance={performance}
                creditType="preCredit"
                pageNumber={preCreditPage}
              />
            </div>
          </IRCPageWrapper>
        )}

        {signatures && sig1Page && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCSignaturesPage
                signatures={signatures}
                pageNumber={sig1Page}
                showAdvisor
                showDisclaimer
                insuredSlice={needsSignatureSplit ? sig1Insureds : allInsureds}
              />
            </div>
          </IRCPageWrapper>
        )}

        {signatures && needsSignatureSplit && sig2Page && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCSignaturesPage
                signatures={signatures}
                pageNumber={sig2Page}
                showAdvisor={false}
                showDisclaimer
                insuredSlice={sig2Insureds}
              />
            </div>
          </IRCPageWrapper>
        )}

        <IRCPageWrapper>
          <div className="irc-pdf-page h-full w-full">
            <IRCDisclosurePage pageNumber={disclosurePage} />
          </div>
        </IRCPageWrapper>

        {performance?.length > 0 && appendixPage && (
          <IRCPageWrapper>
            <div className="irc-pdf-page h-full w-full">
              <IRCAppendixPage
                performance={performance}
                insuranceCompanyName={summary?.entity?.title}
                pageNumber={appendixPage}
              />
            </div>
          </IRCPageWrapper>
        )}
      </div>
    );
  };

  const footer = () => (
    <div className="flex justify-end gap-2 mt-2">
      <Button label="Cancel" severity="secondary" text raised onClick={onClose} />
      <Button
        onClick={handleDownload}
        raised
        disabled={loading || downloadLoading || Boolean(error)}
        style={{
          backgroundColor: primaryColor,
          color: "white",
          border: `1px solid ${primaryColor}`,
        }}
      >
        <span
          className={`w-[20px] mr-1 ${
            downloadLoading
              ? "fadeoutdown animation-duration-1000 animation-iteration-infinite mt-[-15px]"
              : ""
          }`}
        >
          <DownloadIcon />
        </span>
        {downloadLoading ? "Downloading..." : "Download"}
      </Button>
    </div>
  );

  return (
    <div className="irc-download-popup">
      <Dialog
        visible
        modal
        draggable={false}
        closeOnEscape={false}
        closable={false}
        header={<div className="conthrax-font">Preview Interactive Review Center</div>}
        footer={footer}
        contentStyle={{ backgroundColor: "white", padding: "unset", scrollbarWidth: "none" }}
        style={{ width: "50rem" }}
        onHide={onClose}
      >
        {content()}
      </Dialog>
    </div>
  );
}
